import React from "react";

import { faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faEyeSlash, faMinus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, Icon, Text, TextEditor } from "@performyard/py-components";

import SaveStatus from "./SaveStatus";

import { config } from "../config";

type PrivateNotesSectionProps = {
	isPrivateNotesVisible: boolean;
	hasPrivateNotes: boolean;
	isNotesLoading: boolean;
	isInteractionDisabled: boolean;
	isNotesDisabled: boolean;
	privateNoteContent: string;
	showPrivateNotesSaved: boolean;
	showPrivateNotesSaving: boolean;
	isPrivateNotesError: boolean;
	onPrivateNoteChange: (value: string) => void;
	onToggleVisibility: () => void;
};

/**
 * Private notes section with show/hide toggle.
 * Displays the user's private notes editor with autosave status.
 */
const PrivateNotesSection = ({
	isPrivateNotesVisible,
	hasPrivateNotes,
	isNotesLoading,
	isInteractionDisabled,
	isNotesDisabled,
	privateNoteContent,
	showPrivateNotesSaved,
	showPrivateNotesSaving,
	isPrivateNotesError,
	onPrivateNoteChange,
	onToggleVisibility,
}: PrivateNotesSectionProps): React.ReactElement | null => {
	const isAddButtonDisabled = isNotesLoading || (isInteractionDisabled && !hasPrivateNotes);
	const isHideButtonDisabled = isNotesLoading;

	if (!isPrivateNotesVisible) {
		return (
			<ButtonStandard
				iconLeft={faPlus}
				size="medium"
				onClick={onToggleVisibility}
				disabled={isAddButtonDisabled}
				dangerouslySetClassName={
					isAddButtonDisabled ? "disabled:cursor-not-allowed disabled:pointer-events-auto w-fit" : "w-fit"
				}
			>
				{hasPrivateNotes ? "Show Private Notes" : "Add Private Notes"}
			</ButtonStandard>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<div className="flex h-5 items-center justify-between">
					<div className="flex items-center space-x-1">
						<Icon icon={faEyeSlash} size={config.iconSize.large} color="neutral-5" />
						<Text size="paragraph-3" weight="semi-bold">
							My Private Notes
						</Text>
					</div>
					<SaveStatus
						isSaving={showPrivateNotesSaving}
						isSaved={showPrivateNotesSaved}
						hasError={isPrivateNotesError}
					/>
				</div>
				<TextEditor
					id="private-notes-editor"
					initialValue={privateNoteContent}
					onChange={onPrivateNoteChange}
					placeholder="Add private notes..."
					readOnly={isNotesDisabled}
					minHeight={config.layout.textEditorMinHeight}
					maxHeight={config.layout.textEditorMaxHeight}
				/>
			</div>
			<ButtonStandard
				iconLeft={faMinus}
				size="medium"
				variant="outline"
				color="neutral"
				onClick={onToggleVisibility}
				disabled={isHideButtonDisabled}
				dangerouslySetClassName="w-fit"
			>
				Hide Private Notes
			</ButtonStandard>
		</div>
	);
};

export default PrivateNotesSection;
